'use client';

import { ChevronLeft, ChevronRight, Copy, Repeat2 } from 'lucide-react';
import Link from 'next/link';
import { useState } from 'react';

export default function FlashcardPreview() {
  const [isFlipped, setIsFlipped] = useState(false);

  const toggleFlip = () => setIsFlipped((value) => !value);

  return (
    <div
      className="relative rounded-2xl border border-violet-100 bg-white p-3 shadow-xl ring-1 ring-violet-100/50 sm:rounded-3xl sm:p-5 lg:p-6"
      aria-label="Cuplikan antarmuka flashcard interaktif Bahasa Arab"
    >
      <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[10px] text-gray-400 sm:text-[11px]">
        <span className="shrink-0">Flashcard</span>
        <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
        <span className="min-w-0 truncate">bahasa-arab-dasar-a</span>
        <ChevronRight className="h-3 w-3 shrink-0" aria-hidden />
        <span className="shrink-0 font-medium text-gray-700">Latihan</span>
      </div>

      <div className="mt-3 flex items-start gap-2 sm:mt-4 sm:gap-3">
        <div
          className="hidden h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-500 shadow-sm sm:flex"
          aria-hidden
        >
          <ChevronLeft className="h-4 w-4" />
        </div>
        <div
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-violet-100 text-violet-600 sm:h-10 sm:w-10 sm:rounded-xl"
          aria-hidden
        >
          <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <h3 className="line-clamp-2 text-xs font-bold leading-tight text-gray-900 sm:truncate sm:text-sm md:text-base">
              1.1: Flashcard Kata Tunjuk Dekat
            </h3>
            <span className="shrink-0 rounded-full bg-violet-100 px-2 py-0.5 text-[10px] font-semibold text-violet-700 sm:px-2.5 sm:py-1 sm:text-[11px]">
              1 / 10
            </span>
          </div>
          <p className="mt-1 line-clamp-2 text-[10px] leading-snug text-gray-500 sm:mt-0.5 sm:truncate sm:text-[11px]">
            Kartu 1 dari 10 · ketuk kartu atau tekan spasi untuk balik
          </p>
          <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-gray-100">
            <div className="h-full w-[10%] rounded-full bg-violet-500" />
          </div>
        </div>
      </div>

      <div className="relative mt-4 aspect-[4/3] w-full [perspective:1200px] sm:mt-5">
        <button
          type="button"
          onClick={toggleFlip}
          aria-pressed={isFlipped}
          aria-label={isFlipped ? 'Balik kartu untuk melihat depan' : 'Balik kartu untuk melihat belakang'}
          className={`relative h-full w-full cursor-pointer rounded-xl outline-none transition-transform duration-500 ease-out [transform-style:preserve-3d] focus-visible:ring-2 focus-visible:ring-violet-400 focus-visible:ring-offset-2 sm:rounded-2xl ${
            isFlipped ? '[transform:rotateY(180deg)]' : ''
          }`}
        >
          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-gradient-to-b from-white to-gray-50/70 px-3 text-center shadow-[0_8px_24px_-12px_rgba(91,33,182,0.18)] ring-1 ring-gray-100 [backface-visibility:hidden] [-webkit-backface-visibility:hidden] sm:rounded-2xl sm:px-6">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-violet-600 sm:text-[10px]">
                Depan
              </p>
              <p className="mt-2 text-sm font-bold leading-snug text-gray-900 sm:mt-3 sm:text-base lg:text-lg">
                <span>Apa arti dari kalimat </span>
                <span dir="rtl" className="whitespace-nowrap">
                  {'`'}مَا هَذَا؟{'`'}
                </span>
              </p>
            </div>
          </div>

          <div className="absolute inset-0 flex items-center justify-center rounded-xl bg-gradient-to-b from-violet-50 to-white px-3 text-center shadow-[0_8px_24px_-12px_rgba(91,33,182,0.18)] ring-1 ring-violet-100 [transform:rotateY(180deg)] [backface-visibility:hidden] [-webkit-backface-visibility:hidden] sm:rounded-2xl sm:px-6">
            <div>
              <p className="text-[9px] font-bold uppercase tracking-[0.18em] text-violet-600 sm:text-[10px]">
                Belakang
              </p>
              <p className="mt-2 text-sm font-bold leading-snug text-gray-900 sm:mt-3 sm:text-base lg:text-lg">
                Apa ini?
              </p>
              <p className="mt-1 text-[10px] text-gray-500 sm:text-[11px]">
                Terjemahan dari{' '}
                <span dir="rtl" className="whitespace-nowrap font-medium text-gray-700">
                  مَا هَذَا؟
                </span>
              </p>
            </div>
          </div>
        </button>
      </div>

      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-2 gap-y-1 text-[10px] text-gray-500 sm:mt-4 sm:text-[11px]">
        <button
          type="button"
          onClick={toggleFlip}
          className="inline-flex items-center gap-1.5 font-medium text-violet-600 transition hover:text-violet-700"
        >
          <Repeat2 className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
          {isFlipped ? 'Ketuk untuk lihat depan' : 'Ketuk kartu untuk balik'}
        </button>
        <span className="hidden sm:inline" aria-hidden>
          ·
        </span>
        <span className="inline-flex items-center gap-1">
          atau tekan
          <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 text-[9px] font-semibold text-gray-600 sm:px-1.5 sm:text-[10px]">
            Space
          </kbd>
        </span>
      </div>

      <div className="mt-4 flex items-center justify-between gap-2 border-t border-gray-100 pt-3 sm:mt-5 sm:pt-4">
        <Link
          href="/login"
          className="inline-flex items-center gap-1 rounded-lg border border-gray-200 bg-white px-2 py-1 text-[10px] font-semibold text-gray-500 shadow-sm transition hover:bg-gray-50 sm:rounded-xl sm:px-3 sm:py-1.5 sm:text-xs"
        >
          <ChevronLeft className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
          Prev
        </Link>
        <span className="text-[10px] text-gray-500 sm:text-[11px]">9 lagi</span>
        <Link
          href="/login"
          className="inline-flex items-center gap-1 rounded-lg bg-violet-500 px-2 py-1 text-[10px] font-semibold text-white shadow-sm transition hover:opacity-95 sm:rounded-xl sm:px-3 sm:py-1.5 sm:text-xs"
        >
          Next
          <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5" aria-hidden />
        </Link>
      </div>
    </div>
  );
}
